import React, { useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog'
import { Label } from '../ui/label'
import { Combobox } from '../ui/combobox'
import toast, { Toaster } from 'react-hot-toast'
import { useMutation, useQuery } from 'react-query'
import axios from '../../axios/axios'
import { AlertTriangle, Check, Loader2 } from 'lucide-react'
import { Textarea } from '../ui/textarea'
import { Switch } from '../ui/switch'
import { useTranslation } from 'react-i18next'

type Props = {
    open: boolean
    onOpenChange: (o:boolean) => void
    onSuccess?: () => void;
    token?: string | null;
    docid: number;
}

const Forward = ({open=false, onOpenChange, token, docid, onSuccess}:Props) => {
    const { t, i18n } = useTranslation() 
    const [selected_college, setselected_college] = useState<undefined | string>(undefined);
    const [selected_remark, setselected_remark] = useState('');
    const [selected_closeworkflow, setselected_closeworkflow] = useState(true);

    const { data:colleges, isFetching } = useQuery<ForwardUser[] | null>({
        queryKey: [`colleges`],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`users/get`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                if(!Array.isArray(data.object.users)){ throw new Error('users is no array') }
                
                let users = data.object.users.filter((i:ForwardUser)=>!i.blocked)
                return users
            } catch (error) {
                toast.error(t(`Collegas ophalen mislukt`), {id:'colleges'})
                return null
            }
        },
        refetchOnWindowFocus: false
    })

    const { mutate, reset, isLoading, isError, isSuccess } = useMutation({
        mutationFn: async () => {
            toast.loading(`${t('Workflow doorsturen')}...`, {id:'forward'})

            const {data} = await axios.post(`wf/forward`, {
                token: token,
                remark: selected_remark,
                docid: docid,
                userto: selected_college,
                closeworkflow: selected_closeworkflow
            })
            if(data.errorcode!==0){ throw new Error(data.message) }

            onSuccess && onSuccess()
            onOpenChange(false);
            setselected_remark(''); setselected_college(undefined);
            toast.success(`${t('Workflow doorgestuurd')}`, {id:'forward'})
            reset();
            return true;
        },
        onError: () => {
            toast.error(`${t('Workflow doorsturen mislukt')}`, {id:'forward'})
            setTimeout(()=>{ reset() }, 2000)
        }
    })

    function handleClose(open:boolean) {
        onOpenChange(open)

        if(!open) {
            setselected_remark('');
            setselected_college(undefined);
        }
    }

  return (
    <>
    <Toaster />
    <AlertDialog open={open} onOpenChange={(o)=>{handleClose(o)}}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{t('Workflow doorsturen')}</AlertDialogTitle>
                <AlertDialogDescription>{t('Stuur deze workflow door naar een van uw collegas')}</AlertDialogDescription>
            </AlertDialogHeader>
            <div>
                <Label className='flex items-center gap-1 mb-1'>{t('Collega')}{(isFetching && !Array.isArray(colleges)) && <Loader2 className='w-3 h-3 animate-spin text-primary'/>}</Label>
                <Combobox onValueChange={(i)=>{setselected_college(i.value.toString())}} selectedValue={selected_college} options={Array.isArray(colleges) ? colleges.map((c)=>({value:c.username, label:`${c.firstname} ${c.name}`})) : []} placeholder={`${t('Selecteer een collega')}...`}/>
            </div>
            <div>
                <Label htmlFor='remark'>{t('Opmerking')}</Label>
                <Textarea value={selected_remark} onChange={({currentTarget})=>{ setselected_remark(currentTarget.value) }} id='remark'/>
            </div>
            <div className='flex flex-col gap-1'>
                <Label htmlFor='remark'>{t('Workflow voor mij afsluiten')}</Label>
                <Switch checked={selected_closeworkflow} onCheckedChange={(v)=>{setselected_closeworkflow(v)}}/>
            </div>

            <AlertDialogFooter>
                <AlertDialogCancel>{t('Sluiten')}</AlertDialogCancel>
                <AlertDialogAction onClick={()=>{mutate()}} disabled={!selected_college || isError || isLoading} className='gap-2'>
                    {isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}
                    {isError && <AlertTriangle className='w-4 h-4'/>}
                    {isSuccess && <Check className='w-4 h-4'/>}
                    {t('Doorsturen')}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

export default Forward